/**
 * PreviewRowHoverCard — portal-mounted hover card with smart side placement.
 *
 * Opens on hover/focus with a small delay; positions itself on the side of
 * the trigger row that has more space (typically the left, since the
 * preview rows live in the right-side panel).
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, ReactNode } from 'react';
import { createPortal } from 'react-dom';

import PreviewMetricsCard from './PreviewMetricsCard';
import type { PreviewRow } from '../preview-row.types';
import { usePreviewRowHover } from './usePreviewRowHover';

export interface PreviewRowHoverCardProps {
  name: string;
  row: PreviewRow | null;
  children: ReactNode;
  /** Applied to the hover trigger wrapper — lets callers scope the trigger
   *  to just the metric cells (and keep their grid layout). */
  className?: string;
}

const PreviewRowHoverCard: FC<PreviewRowHoverCardProps> = ({
  name,
  row,
  children,
  className,
}) => {
  const {
    triggerRef,
    popupRef,
    cardRef,
    open,
    shellStyle,
    handleTriggerEnter,
    handleTriggerLeave,
    handlePopupEnter,
    handlePopupLeave,
    handleTriggerBlur,
  } = usePreviewRowHover(Boolean(row));

  return (
    <div
      ref={triggerRef}
      className={className}
      onMouseEnter={handleTriggerEnter}
      onMouseLeave={handleTriggerLeave}
      onFocus={handleTriggerEnter}
      onBlur={handleTriggerBlur}
    >
      {children}
      {open && row
        ? createPortal(
            <div
              ref={popupRef}
              role="presentation"
              className="fixed z-[9999]"
              style={{
                top: shellStyle?.top ?? -9999,
                left: shellStyle?.left ?? -9999,
                paddingRight: shellStyle?.paddingRight,
                paddingLeft: shellStyle?.paddingLeft,
                opacity: shellStyle ? 1 : 0,
                pointerEvents: shellStyle ? 'auto' : 'none',
              }}
              onMouseEnter={handlePopupEnter}
              onMouseLeave={handlePopupLeave}
            >
              <div ref={cardRef}>
                <PreviewMetricsCard name={name} row={row} />
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};

export default PreviewRowHoverCard;
